<script setup>
import useVuelidate from '@vuelidate/core'
import {helpers, integer, maxLength, minLength, required} from '@vuelidate/validators'
import {computed, ref, watch} from 'vue'

import DropdownCountry from '@/components/form/DropdownCountry.vue'
import InputText from '@/components/form/InputText.vue'
import {useAddressStore} from '@/stores/AddressStore'
import {useSettingsStore} from '@/stores/SettingsStore'
import {useValidators} from '@/useValidators'

const props = defineProps({
  submitLabel: {
    type: String,
    required: true
  },
  modelValue: {
    type: Object,
    default: () => {
      return {
        id: null,
        first_name: '',
        last_name: '',
        address: '',
        number: '',
        zip: '',
        city: '',
        country: window.settings.version,
        phone_number: '',
        company_name: '',
        company_id: '',
        tax_id: '',
        vat_id: '',
      }
    }
  },
  alwaysShowCompany: {
    type: Boolean,
    default: false
  },
  isSameAsShippingAddress: {
    type: Boolean,
    default: false
  }
})

const isInvoiceAddressCompanyVisible = ref(props.alwaysShowCompany)

const {phoneValidator} = useValidators()
const addressStore = useAddressStore()
const settingsStore = useSettingsStore()

const validIco = (value) => addressStore.isICOValid(value)

const isSKPage = computed(() => settingsStore.getVersion === 'SK')

const validationRules = computed(() => {
  let rules = {
    first_name: {required, maxLength: maxLength(50)},
    last_name: {required, maxLength: maxLength(50)},
    phone_number: {
      required,
      phone: phoneValidator
    },
    address: {required, minLength: minLength(3), maxLength: maxLength(50)},
    number: {required, maxLength: maxLength(50)},
    zip: {
      required,
      integer,
      minLength: minLength(5),
      maxLength: maxLength(5)
    },
    country: {required},
    city: {required, maxLength: maxLength(50)},
    company_name: {maxLength: maxLength(50)},
    company_id: {},
    tax_id: {},
    vat_id: {},
  }

  if (isInvoiceAddressCompanyVisible.value) {
    rules.company_name = {required, maxLength: maxLength(50)}
    rules.company_id = {
      required,
      maxLength: maxLength(50),
      validIco: helpers.withMessage('Invalid company id', validIco)
    }
    rules.tax_id = {maxLength: maxLength(50)}

    if (isSKPage.value) {
      rules.vat_id = {
        validVatId: helpers.withMessage('Invalid VAT ID', (val) => !val || addressStore.isValidSKVAT(val))
      }
    }
  }

  return rules
})

const address = computed({
  get() {
    return props.modelValue
  },
  set(newAddress) {
    emits('update:modelValue', newAddress)
  }
})

const $vI = useVuelidate(validationRules, address, {$stopPropagation: true})

const emits = defineEmits(['update:modelValue', 'onSuccessfulUpdate'])

const handleUpdate = () => {
  $vI.value.$touch()

  if (!$vI.value.$invalid) {
    emits('onSuccessfulUpdate')
  }
}

const isValid = (resetAfterCheck = true) => {
  $vI.value.$touch()
  const valid = !$vI.value.$invalid
  if (resetAfterCheck) {
    $vI.value.$reset()
  }
  return valid
}

defineExpose({
  isValid,
  isInvoiceAddressCompanyVisible
})

watch(
    [() => props.isSameAsShippingAddress, isInvoiceAddressCompanyVisible],
    ([isSameAsShipping, companyDetailsVisible]) => {
      if (!companyDetailsVisible) {
        // Reset company-related fields when details are not visible
        emits('update:modelValue', {...address.value, company_name: '', company_id: '', tax_id: '', vat_id: ''})
        return
      }

      // Reset validation
      $vI.value.company_name.$reset()
      $vI.value.company_id.$reset()

      if (isSameAsShipping) {
        const companyName = addressStore.address.company_name || addressStore.addressDraft.company_name || ''
        emits('update:modelValue', {
          ...address.value,
          company_name: companyName,
          company_id: '',
          tax_id: '',
          vat_id: '',
        })
      }
    }
)
</script>

<template>
  <div class="step-address__form">
    <p v-if="!addressStore.hasInvoiceAddress" class="input-group__description">
      {{
        $t(
            'If you need an invoice, please add the billing address, otherwise a simplified tax receipt will be issued.'
        )
      }}
    </p>

    <template v-if="!isSameAsShippingAddress">
      <InputText
          v-model="$vI.first_name.$model"
          name="first_name"
          autocomplete="given-name"
          :validator="$vI.first_name"
          :label="$t('First name')"
          :placeholder="$t('First name placeholder')"
      />

      <InputText
          v-model="$vI.last_name.$model"
          name="last_name"
          autocomplete="family-name"
          :validator="$vI.last_name"
          :label="$t('Last name')"
          :placeholder="$t('Last name placeholder')"
      />

      <InputText
          v-model="$vI.phone_number.$model"
          name="phone_number"
          autocomplete="tel"
          type="tel"
          :phone-prefix="settingsStore.defaultPhonePrefix"
          :validator="$vI.phone_number"
          :label="$t('Phone number')"
          :placeholder="$t('Phone number placeholder')"
      />

      <InputText
          v-model="$vI.address.$model"
          name="address"
          autocomplete="address-line1"
          :validator="$vI.address"
          :label="$t('Street')"
          :placeholder="$t('Street placeholder')"
      />

      <InputText
          v-model="$vI.number.$model"
          name="number"
          autocomplete="address-line2"
          :validator="$vI.number"
          :label="$t('Street number')"
          :placeholder="$t('Street number placeholder')"
      />

      <InputText
          v-model="$vI.city.$model"
          name="city"
          autocomplete="address-level2"
          :validator="$vI.city"
          :label="$t('City')"
          :placeholder="$t('City placeholder')"
      />

      <InputText
          v-model="$vI.zip.$model"
          name="zip"
          autocomplete="postal-code"
          :validator="$vI.zip"
          :label="$t('ZIP code')"
          :placeholder="$t('ZIP code placeholder')"
      />

      <DropdownCountry v-model="$vI.country.$model"/>
    </template>

    <div v-if="!alwaysShowCompany" class="input-group checkbox-group">
      <div class="checkbox checkbox--medium">
        <input
            v-model="isInvoiceAddressCompanyVisible"
            type="checkbox"
            class="checkbox__input"
            id="invoiceCheckbox"
            data-content="paymentContent2"
        />
        <span class="checkbox__indicator"></span>
      </div>

      <div class="input-group__label-wrapper">
        <label class="checkbox__label" for="invoiceCheckbox">
          {{ $t('I have company details') }}
        </label>
      </div>
    </div>

    <div
        :class="{
        active: isInvoiceAddressCompanyVisible
      }"
        class="input-group__content-wrapper"
    >
      <InputText
          v-model="$vI.company_name.$model"
          name="company_name"
          autocomplete="organization"
          :validator="$vI.company_name"
          :label="$t('Company name')"
          :placeholder="$t('Company name placeholder')"
      />

      <InputText
          v-model="$vI.company_id.$model"
          name="company_id"
          autocomplete="organization-id"
          :validator="$vI.company_id"
          :label="$t('Company ID')"
          :placeholder="$t('Company ID placeholder')"
      />

      <InputText
          v-model="$vI.tax_id.$model"
          name="tax_id"
          autocomplete="tax-id"
          :validator="$vI.tax_id"
          :label="$t('Tax ID')"
          :placeholder="$t('Tax ID placeholder')"
      />

      <InputText
          v-if="isSKPage"
          v-model="$vI.vat_id.$model"
          name="vat_id"
          autocomplete="vat-id"
          :validator="$vI.vat_id"
          :label="$t('VAT ID')"
          :placeholder="$t('VAT ID placeholder')"
      />
    </div>

    <div class="input-group button-group">
      <button
          @click="handleUpdate"
          class="button button--large button--primary step-address__button"
      >
        {{ submitLabel }}
      </button>
    </div>
  </div>
</template>
